
import { supabase } from '@/integrations/supabase/client';
import { Movie } from '@/types/movie';
import { LIST_NAMES } from '@/hooks/search/types/listConstants';
import { fromDbRow } from '@/utils/fromDbRow';

interface SearchAnalytics {
  wasProgressive: boolean;
  searchQuality: 'exact' | 'fuzzy' | 'progressive';
  performanceMs: number;
  primaryDepartment?: string;
  creditsRatio?: string;
}

export interface UnifiedSearchResult {
  [LIST_NAMES.FAVORITES]: Movie[];
  [LIST_NAMES.WATCHLIST]: Movie[];
  [LIST_NAMES.UNSEEN_GEMS]: Movie[];
  all: Movie[];
  searchAnalytics?: SearchAnalytics;
}

export interface SearchResponse {
  [LIST_NAMES.FAVORITES]: Movie[];
  [LIST_NAMES.WATCHLIST]: Movie[];
  [LIST_NAMES.UNSEEN_GEMS]: Movie[];
  all: Movie[];
  searchAnalytics?: SearchAnalytics;
}

export async function fetchUnifiedResults(query: string, signal?: AbortSignal): Promise<UnifiedSearchResult> {
  try {
    console.log('→ Starting TMDB search for query:', query);
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    // Call the search_movies edge function
    const { data, error } = await supabase.functions.invoke('search_movies', {
      body: { 
        query,
        uid: user?.id 
      }
    });

    // Check if the request was aborted
    if (signal?.aborted) {
      throw new Error('AbortError');
    }

    if (error) {
      console.error('← Edge function error:', error);
      throw new Error(`Search failed: ${error.message}`);
    }

    if (!data) {
      console.error('← No data returned from edge function');
      throw new Error('No data returned from search');
    }

    console.log('← Raw edge function response:', {
      allCount: data.all?.length || 0,
      favoritesCount: data.favorites?.length || 0,
      watchlistCount: data.watchlist?.length || 0,
      unseenGemsCount: data.unseen_gems?.length || 0,
      firstResult: data.all?.[0]
    });

    // DIAGNOSTIC: Check the structure of the first result to understand the data format
    if (data.all?.[0]) {
      console.log('🔍 First result structure analysis:', {
        hasSnakeCaseTmdbId: 'tmdb_id' in data.all[0],
        hasCamelCaseTmdbId: 'tmdbId' in data.all[0],
        hasSnakeCasePlanToWatch: 'plan_to_watch' in data.all[0],
        hasCamelCasePlanToWatch: 'planToWatch' in data.all[0],
        allKeys: Object.keys(data.all[0])
      });
    }

    // CRITICAL FIX: Conditionally apply mapping based on data format
    // If data already has camelCase fields, don't apply fromDbRow mapping
    const isSnakeCaseData = data.all?.[0] && 'tmdb_id' in data.all[0];
    
    console.log('🔧 Data format detection:', {
      isSnakeCaseData,
      willApplyMapping: isSnakeCaseData
    });

    let mappedResult: UnifiedSearchResult;

    if (isSnakeCaseData) {
      // Data is in snake_case, apply fromDbRow mapping
      console.log('↪ Applying fromDbRow mapping for snake_case data');
      mappedResult = {
        [LIST_NAMES.FAVORITES]: (data.favorites || []).map(fromDbRow),
        [LIST_NAMES.WATCHLIST]: (data.watchlist || []).map(fromDbRow),
        [LIST_NAMES.UNSEEN_GEMS]: (data.unseen_gems || []).map(fromDbRow),
        all: (data.all || []).map(fromDbRow),
        searchAnalytics: data.searchAnalytics
      };
    } else {
      // Data is already in camelCase, use as-is but ensure correct structure
      console.log('↪ Using camelCase data as-is');
      mappedResult = {
        [LIST_NAMES.FAVORITES]: data.favorites || [],
        [LIST_NAMES.WATCHLIST]: data.watchlist || [],
        [LIST_NAMES.UNSEEN_GEMS]: data.unseen_gems || [],
        all: data.all || [],
        searchAnalytics: data.searchAnalytics
      };
    }

    console.log('↪ Final mapped result:', {
      allCount: mappedResult.all.length,
      favoritesCount: mappedResult[LIST_NAMES.FAVORITES].length,
      watchlistCount: mappedResult[LIST_NAMES.WATCHLIST].length,
      unseenGemsCount: mappedResult[LIST_NAMES.UNSEEN_GEMS].length,
      firstMappedResult: mappedResult.all[0]
    });

    // DIAGNOSTIC: Verify tmdbId is present in final results
    if (mappedResult.all[0]) {
      console.log('✅ Final result verification:', {
        hasTmdbId: 'tmdbId' in mappedResult.all[0],
        tmdbIdValue: mappedResult.all[0].tmdbId,
        title: mappedResult.all[0].title
      });
    }

    return mappedResult;
  } catch (error) {
    // Re-throw AbortError as-is so it can be handled properly
    if (error instanceof Error && (error.name === 'AbortError' || error.message === 'AbortError')) {
      const abortError = new Error('AbortError');
      abortError.name = 'AbortError';
      throw abortError;
    }
    
    console.error('Search service error:', error);
    throw error instanceof Error ? error : new Error('Search failed');
  }
}
